import { DeleteIcon, LoadingIcon } from '@assets/icons';
import { CopyIconButton, IconButton } from '@components/button';
import { useModal } from '@components/modal';
import { DateDisplay, Typography } from '@components/typography';
import { SSHKey, useGetSSHKeysQuery } from '@generated/graphql';
import { Box, Divider } from '@mui/material';
import { AddSSHKeyModal } from './AddSSHKeyModal';
import { DeleteSSHKeyModal } from './DeleteSSHKeyModal';
import { KeysContainer } from './KeysContainer';

const SSHKeyRow = ({ sshKey, refetch }: { sshKey: SSHKey; refetch: () => void }) => {
  const { showModal: showDeleteModal } = useModal({
    component: DeleteSSHKeyModal,
    componentProps: { name: sshKey.name, onClose: refetch },
  });

  return (
    <>
      <Typography variant="body1" sx={{ color: 'text.paper' }}>
        {sshKey.name}
      </Typography>
      <DateDisplay date={sshKey.createdAt} sx={{ color: 'text.paper' }} />
      <Typography variant="body2" sx={{ color: 'text.paper' }} handleOverFlow>
        {sshKey.key}
      </Typography>
      <Box>
        <CopyIconButton value={sshKey.key} />
      </Box>
      <IconButton Icon={DeleteIcon} onClick={() => showDeleteModal()} />
    </>
  );
};

export const SSHKeys = () => {
  const { data, loading, error, refetch } = useGetSSHKeysQuery();
  const keys = data?.getSSHKeys || [];

  const { showModal } = useModal({
    component: AddSSHKeyModal,
    componentProps: { onClose: refetch },
    modalProps: { title: 'Add SSH Key' },
  });

  const onCreate = () => {
    showModal();
  };

  return (
    <KeysContainer
      title="SSH Keys"
      onCreate={onCreate}
      count={loading ? null : keys.length}
      actionButtonLabel="Add SSH key"
    >
      {loading ? (
        <LoadingIcon />
      ) : error ? (
        <Typography variant="body2">Error loading SSH keys</Typography>
      ) : keys.length === 0 ? (
        <Typography variant="body2" sx={{ display: 'inline-block' }}>
          No SSH keys found
        </Typography>
      ) : (
        <Box
          gap={2}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto auto auto 1fr min-content',
            columnGap: 8,
            gridAutoRows: 'auto',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              px: 2,
              display: 'grid',
              gridColumn: 'span 5',
              gridTemplateColumns: 'subgrid',
              alignItems: 'center',
            }}
          >
            <Typography variant="caption1">Name</Typography>
            <Typography variant="caption1">Created</Typography>
            <Typography variant="caption1">Key</Typography>
          </Box>
          <Divider sx={{ gridColumn: 'span 5' }} />
          {keys.map((sshKey) => (
            <Box
              key={sshKey.name}
              sx={{
                py: 1,
                px: 2,
                display: 'grid',
                gridColumn: 'span 5',
                gridTemplateColumns: 'subgrid',
                alignItems: 'center',
                '&:hover': { backgroundColor: 'backgroundHover.paper' },
              }}
            >
              <SSHKeyRow sshKey={sshKey} refetch={refetch} />
            </Box>
          ))}
        </Box>
      )}
    </KeysContainer>
  );
};
